import type { CautionBancaire, StatutTache, Tache } from '../db/modele'
import type { BaseOutbox, ChantierLocal, DevisLocal } from '../outbox/base'
import { creer, lire, lister, modifier, supprimer } from '../outbox/depot'
import { nouvelId } from './id'
import { cleOuvrier, couleurSuivante, debutMinimal, deplacerTache, finDe, fixerPeriode, jourValide, planifiee, tachesDepuisDevis } from './planning'

// Chantiers : écriture locale d'abord (outbox). Les tâches sont dans le document du chantier ; chaque
// modification relit l'état en base dans la transaction, pour que deux gestes rapprochés ne s'écrasent pas.

/** Chantiers en cours d'abord, puis terminés ; par nom. */
export async function listerChantiers(base: BaseOutbox): Promise<ChantierLocal[]> {
  return (await lister(base, 'chantiers')).sort((a, b) => Number(a.termine) - Number(b.termine) || a.nom.localeCompare(b.nom, 'fr'))
}

/** Chantier libre (sans devis), ou issu d'un devis signé dont les lots deviennent les tâches. */
export async function creerChantier(base: BaseOutbox, saisie: { nom: string; clientId?: string; adresse?: string; devis?: DevisLocal }): Promise<ChantierLocal> {
  const nom = saisie.nom.trim()
  if (!nom) throw new Error('Le chantier a besoin d’un nom.')
  const existants = await lister(base, 'chantiers')
  const { devis } = saisie
  return creer(base, 'chantiers', {
    id: nouvelId(),
    nom,
    couleur: couleurSuivante(existants),
    termine: false,
    taches: devis ? tachesDepuisDevis(devis.lignes) : [],
    ...((saisie.clientId ?? devis?.clientId) && { clientId: saisie.clientId ?? devis!.clientId }),
    ...(devis && { devisId: devis.id }),
    ...(saisie.adresse?.trim() && { adresse: saisie.adresse.trim() }),
  })
}

/** Chantiers déjà créés à partir d'un devis (un devis peut en avoir plusieurs : tranches, sites). */
export async function chantiersDuDevis(base: BaseOutbox, devisId: string): Promise<ChantierLocal[]> {
  return (await base.chantiers.where('devisId').equals(devisId).toArray()).filter((c) => !c._deleted)
}

export const renommerChantier = async (base: BaseOutbox, id: string, nom: string, adresse?: string) => {
  if (!nom.trim()) throw new Error('Le chantier a besoin d’un nom.')
  return modifier(base, 'chantiers', id, { nom: nom.trim(), adresse: adresse?.trim() || undefined })
}

/** Date de réception des travaux (vide pour l'effacer) et réserves non levées. */
export const fixerReception = async (base: BaseOutbox, id: string, receptionLe: string | undefined, reserves: boolean) => {
  if (receptionLe && !jourValide(receptionLe)) throw new Error('La date de réception est invalide.')
  return modifier(base, 'chantiers', id, (c) => {
    if (c.retenueLibereeLe && receptionLe && c.retenueLibereeLe < receptionLe) throw new Error('La retenue est déjà libérée avant cette date de réception.')
    return { receptionLe: receptionLe || undefined, reserves: reserves || undefined, ...(!receptionLe && { retenueLibereeLe: undefined }) }
  })
}

/** Jour où la retenue de garantie a été libérée ; `undefined` la remet en attente. Exige une réception saisie. */
export const libererRetenue = async (base: BaseOutbox, id: string, jour: string | undefined) => {
  if (jour && !jourValide(jour)) throw new Error('La date de libération est invalide.')
  return modifier(base, 'chantiers', id, (c) => {
    if (jour && !c.receptionLe) throw new Error('Saisissez d’abord la date de réception des travaux.')
    if (jour && c.receptionLe && jour < c.receptionLe) throw new Error('La retenue ne peut pas être libérée avant la réception.')
    return { retenueLibereeLe: jour }
  })
}

/** Caution bancaire du chantier (`undefined` pour la retirer). */
export const fixerCaution = async (base: BaseOutbox, id: string, caution: CautionBancaire | undefined) => {
  if (caution) {
    if (!Number.isInteger(caution.montantCents) || caution.montantCents < 1) throw new Error('Le montant de la caution doit être supérieur à zéro.')
    if (!caution.banque.trim()) throw new Error('Indiquez la banque qui a émis la caution.')
    if (caution.expireLe && !jourValide(caution.expireLe)) throw new Error('La date de fin de validité est invalide.')
  }
  const propre = caution && { montantCents: caution.montantCents, banque: caution.banque.trim(), ...(caution.reference?.trim() && { reference: caution.reference.trim() }), ...(caution.expireLe && { expireLe: caution.expireLe }) }
  return modifier(base, 'chantiers', id, { caution: propre })
}

export const terminerChantier = (base: BaseOutbox, id: string, termine: boolean) => modifier(base, 'chantiers', id, { termine })
export const supprimerChantier = (base: BaseOutbox, id: string) => supprimer(base, 'chantiers', id)
export const lireChantier = (base: BaseOutbox, id: string) => lire(base, 'chantiers', id)

function tacheDe(taches: Tache[], id: string): Tache {
  const t = taches.find((x) => x.id === id)
  if (!t) throw new Error('Tâche introuvable.')
  return t
}

/** Remplace une tâche par le résultat de `calcul` (fonction de l'état en base). */
function changerTache(base: BaseOutbox, chantierId: string, tacheId: string, calcul: (t: Tache, toutes: Tache[]) => Tache) {
  return modifier(base, 'chantiers', chantierId, (c) => ({ taches: c.taches.map((t) => (t.id === tacheId ? calcul(t, c.taches) : t)) }))
}

export const ajouterTache = async (base: BaseOutbox, chantierId: string, libelle: string) => {
  if (!libelle.trim()) throw new Error('La tâche a besoin d’un libellé.')
  return modifier(base, 'chantiers', chantierId, (c) => ({
    taches: [...c.taches, { id: nouvelId(), libelle: libelle.trim(), statut: 'a_faire' as const, ouvriers: [] }],
  }))
}

/** Retirer une tâche détache celles qui la suivaient (elles ne dépendent plus de rien). */
export const retirerTache = (base: BaseOutbox, chantierId: string, tacheId: string) =>
  modifier(base, 'chantiers', chantierId, (c) => ({
    taches: c.taches.filter((t) => t.id !== tacheId).map((t) => (t.apresId === tacheId ? { ...t, apresId: undefined } : t)),
  }))

export const renommerTache = async (base: BaseOutbox, chantierId: string, tacheId: string, libelle: string) => {
  if (!libelle.trim()) throw new Error('La tâche a besoin d’un libellé.')
  return changerTache(base, chantierId, tacheId, (t) => ({ ...t, libelle: libelle.trim() }))
}

export const changerStatut = (base: BaseOutbox, chantierId: string, tacheId: string, statut: StatutTache) =>
  changerTache(base, chantierId, tacheId, (t) => ({ ...t, statut }))

/** Noms d'ouvriers nettoyés : sans vide ni doublon (casse, accents et espaces ignorés). */
export function nettoyerOuvriers(noms: string[]): string[] {
  const vus = new Set<string>()
  return noms.map((n) => n.trim().replace(/\s+/g, ' ')).filter((n) => n && !vus.has(cleOuvrier(n)) && vus.add(cleOuvrier(n)))
}

export const affecterOuvriers = (base: BaseOutbox, chantierId: string, tacheId: string, noms: string[]) =>
  changerTache(base, chantierId, tacheId, (t) => ({ ...t, ouvriers: nettoyerOuvriers(noms) }))

/** Remplace un ouvrier par un autre sur une tâche (sans doublon si le remplaçant y était déjà). */
export const remplacerOuvrier = (base: BaseOutbox, chantierId: string, tacheId: string, de: string, par: string) =>
  changerTache(base, chantierId, tacheId, (t) => ({ ...t, ouvriers: nettoyerOuvriers(t.ouvriers.map((o) => (cleOuvrier(o) === cleOuvrier(de) ? par : o))) }))

export const fixerCreneau = (base: BaseOutbox, chantierId: string, tacheId: string, creneau: Tache['creneau']) =>
  changerTache(base, chantierId, tacheId, (t) => ({ ...t, creneau: creneau === 'journee' ? undefined : creneau }))

function verifierDebut(t: Tache, debut: string, toutes: Tache[]) {
  if (!jourValide(debut)) throw new Error('Date invalide.')
  const min = debutMinimal(t, toutes)
  if (min && debut < min) {
    const avant = toutes.find((x) => x.id === t.apresId)!
    throw new Error(`« ${t.libelle} » suit « ${avant.libelle} » : au plus tôt le ${min.split('-').reverse().join('/')}.`)
  }
}

/** Planifie ou déplace une tâche à partir d'un jour, en gardant sa durée ; respecte sa dépendance. Une tâche à faire passe en cours le jour venu : le statut reste celui que l'artisan choisit. */
export const deplacerVers = (base: BaseOutbox, chantierId: string, tacheId: string, debut: string) =>
  changerTache(base, chantierId, tacheId, (t, toutes) => {
    verifierDebut(t, debut, toutes)
    return deplacerTache(t, debut)
  })

export const fixerDates = (base: BaseOutbox, chantierId: string, tacheId: string, debut: string, fin: string) =>
  changerTache(base, chantierId, tacheId, (t, toutes) => {
    if (!jourValide(fin)) throw new Error('Date invalide.')
    verifierDebut(t, debut, toutes)
    return fixerPeriode(t, debut, fin)
  })

export const dePlanifier = (base: BaseOutbox, chantierId: string, tacheId: string) =>
  changerTache(base, chantierId, tacheId, (t) => ({ ...t, debut: undefined, fin: undefined, creneau: undefined }))

/**
 * Dépendance fin → début : `apresId` doit exister, ne pas être la tâche elle-même ni former une boucle. Si la
 * tâche est déjà planifiée avant la fin de la précédente, elle est décalée (durée conservée).
 */
export const fixerDependance = (base: BaseOutbox, chantierId: string, tacheId: string, apresId: string | undefined) =>
  modifier(base, 'chantiers', chantierId, (c) => {
    if (apresId) {
      tacheDe(c.taches, apresId)
      for (let x: string | undefined = apresId; x; x = c.taches.find((t) => t.id === x)?.apresId) {
        if (x === tacheId) throw new Error('Une tâche ne peut pas dépendre d’elle-même, même par un détour.')
      }
    }
    const taches = c.taches.map((t) => (t.id === tacheId ? { ...t, apresId } : t))
    const t = taches.find((x) => x.id === tacheId)!
    const min = debutMinimal(t, taches)
    return { taches: taches.map((x) => (x.id === tacheId && min && planifiee(x) && x.debut < min ? deplacerTache(x, min) : x)) }
  })

/**
 * Lots des avenants signés du devis qui ne sont pas encore des tâches du chantier : (avenant, ligne) absents des
 * tâches. Un lot ajouté puis retiré à la main n'est pas réajouté tant qu'on ne le demande pas : seul « ajouter »
 * relit les avenants.
 */
export function lotsAvenantsAAjouter(chantier: Pick<ChantierLocal, 'taches'>, avenants: Pick<DevisLocal, 'id' | 'statut' | 'lignes'>[]): { avenantId: string; ligneIndex: number; libelle: string }[] {
  return avenants
    .filter((a) => a.statut === 'signe')
    .flatMap((a) => a.lignes.flatMap((l, ligneIndex) => (l.quantite < 0 ? [] : [{ avenantId: a.id, ligneIndex, libelle: l.designation }])))
    .filter((l) => !chantier.taches.some((t) => t.avenantId === l.avenantId && t.ligneIndex === l.ligneIndex))
}

/** Ajoute les lots des avenants signés comme tâches à faire. Renvoie le nombre de tâches ajoutées. */
export async function ajouterLotsAvenants(base: BaseOutbox, chantierId: string, avenants: Pick<DevisLocal, 'id' | 'statut' | 'lignes'>[]): Promise<number> {
  let ajoutees = 0
  await modifier(base, 'chantiers', chantierId, (c) => {
    const lots = lotsAvenantsAAjouter(c, avenants)
    ajoutees = lots.length
    return { taches: [...c.taches, ...lots.map((l) => ({ id: nouvelId(), libelle: l.libelle, statut: 'a_faire' as const, ouvriers: [], ligneIndex: l.ligneIndex, avenantId: l.avenantId }))] }
  })
  return ajoutees
}

/** Fin de période d'un chantier : dernier jour de ses tâches planifiées. */
export function periodeChantier(taches: Tache[]): { debut: string; fin: string } | null {
  const planifiees = taches.filter(planifiee)
  if (!planifiees.length) return null
  return { debut: planifiees.map((t) => t.debut).sort()[0]!, fin: planifiees.map(finDe).sort().at(-1)! }
}

export type SaisieTache = {
  libelle: string
  statut: StatutTache
  debut?: string
  fin?: string
  creneau?: Tache['creneau']
  ouvriers: string[]
  apresId?: string
}

/**
 * Enregistre d'un coup la fiche d'une tâche (une seule écriture, donc une seule opération) : mêmes règles que les
 * gestes isolés (libellé, dates, dépendance sans boucle ni début avant la fin de la précédente).
 */
export const enregistrerTache = (base: BaseOutbox, chantierId: string, tacheId: string, saisie: SaisieTache) =>
  modifier(base, 'chantiers', chantierId, (c) => {
    if (!saisie.libelle.trim()) throw new Error('La tâche a besoin d’un libellé.')
    tacheDe(c.taches, tacheId)
    if (saisie.apresId) {
      tacheDe(c.taches, saisie.apresId)
      for (let x: string | undefined = saisie.apresId; x; x = c.taches.find((t) => t.id === x)?.apresId) {
        if (x === tacheId) throw new Error('Une tâche ne peut pas dépendre d’elle-même, même par un détour.')
      }
    }
    if (!saisie.debut && saisie.fin) throw new Error('Indiquez aussi le jour de début.')
    const toutes = c.taches.map((t) => (t.id === tacheId ? { ...t, apresId: saisie.apresId } : t))
    const courante = toutes.find((t) => t.id === tacheId)!
    if (saisie.debut) {
      verifierDebut(courante, saisie.debut, toutes)
      if (saisie.fin && !jourValide(saisie.fin)) throw new Error('Date invalide.')
    }
    const planifiee_ = saisie.debut ? fixerPeriode(courante, saisie.debut, saisie.fin || saisie.debut) : { ...courante, debut: undefined, fin: undefined }
    return {
      taches: toutes.map((t) =>
        t.id === tacheId
          ? {
              ...planifiee_,
              libelle: saisie.libelle.trim(),
              statut: saisie.statut,
              creneau: saisie.debut && saisie.creneau !== 'journee' ? saisie.creneau : undefined,
              ouvriers: nettoyerOuvriers(saisie.ouvriers),
            }
          : t,
      ),
    }
  })
